import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { api, authStore, exchangeOnboarding, login as apiLogin, logoutSession } from "../lib/api";
import type { Bootstrap } from "../lib/types";

interface AppContextValue {
  bootstrap: Bootstrap | null;
  loading: boolean;
  authenticated: boolean;
  error: string;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  switchOrganisation: (code: string) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Switching Accounts may leave a previous customer's selected tenant in
      // localStorage. Never force that tenant into the next user's session.
      let data: Bootstrap;
      try {
        data = await api.get<Bootstrap>("/api/v1/backoffice/bootstrap/");
      } catch (cause) {
        if ((cause as { status?: number }).status !== 403 || !authStore.getOrganisation()) {
          throw cause;
        }
        authStore.clearOrganisation();
        data = await api.get<Bootstrap>("/api/v1/backoffice/bootstrap/", null);
      }
      setBootstrap(data);
      if (data.active_organisation?.code) {
        authStore.setOrganisation(data.active_organisation.code);
      }
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger le back-office.");
      if ([401, 403].includes((err as { status?: number }).status || 0)) {
        authStore.clearToken();
        setBootstrap(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const handoff = hash.get("handoff");
    if (handoff && !authStore.getToken()) {
      setLoading(true);
      exchangeOnboarding(handoff)
        .then(() => {
          window.history.replaceState({}, "", window.location.pathname + window.location.search);
          return refresh();
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "Session de démarrage invalide.");
          setLoading(false);
        });
      return;
    }
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      setLoading(true);
      try {
        await apiLogin(username, password);
        await refresh();
      } finally {
        setLoading(false);
      }
    },
    [refresh]
  );

  const logout = useCallback(() => {
    // After an explicit logout, the login page must not sign back in silently.
    try { sessionStorage.setItem("sso-manual", "1"); } catch { /* storage off */ }
    // Legacy token-only sessions have no server cookie to revoke; do not
    // let CSRF rejection trap users during the staged SSO migration.
    if (authStore.getToken() && !document.cookie.includes("csrftoken=")) {
      authStore.clearToken();
      authStore.clearOrganisation();
      setBootstrap(null);
      return;
    }
    void logoutSession()
      .then(() => {
        authStore.clearToken();
        authStore.clearOrganisation();
        setBootstrap(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Déconnexion impossible."));
  }, []);

  const switchOrganisation = useCallback(async (code: string) => {
    if (!code) throw new Error("Choisissez une entreprise valide.");
    setLoading(true);
    try {
      // Validate the requested tenant on the server BEFORE persisting a
      // selected organisation. An X-Organisation-ID is not an authorization.
      const data = await api.get<Bootstrap>("/api/v1/backoffice/bootstrap/", code);
      if (data.active_organisation?.code !== code) {
        throw new Error("Accès à cette entreprise non confirmé.");
      }
      authStore.setOrganisation(code);
      setBootstrap(data);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Changement d'entreprise impossible.");
      throw cause;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      bootstrap,
      loading,
      authenticated: Boolean(bootstrap),
      error,
      login,
      logout,
      refresh,
      switchOrganisation
    }),
    [bootstrap, loading, error, login, logout, refresh, switchOrganisation]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp doit être utilisé dans AppProvider");
  return value;
}
